import { BRAND } from '../brand.js';
import { getFeatures } from '../features/index.js';
import { currentLang, dirFor, t } from '../i18n/index.js';
import { getHeadings, getLandmarks, getLinks, jumpTo } from '../nav/outline.js';
import type { OutlineItem } from '../nav/outline.js';
import type {
  ChoiceFeature,
  GroupId,
  StepperFeature,
  ToggleFeature,
  WidgetConfig,
} from '../types.js';
import { chevronIcon, closeIcon, el } from './dom.js';
import { telHref } from './phone.js';

export interface PanelHandlers {
  getLevel(id: string): number;
  /**
   * Returns false if the feature threw and the change was rolled back.
   *
   * The panel used to announce the new state unconditionally right after
   * calling this, which meant a failed toggle still said "on" — the panel
   * confidently describing something that had not happened, to the one user who
   * has no way to check. The caller announces the failure itself; callers here
   * only announce success.
   */
  setLevel(id: string, level: number): boolean;
  reset(): void;
  close(): void;
  announce(message: string): void;
}

/**
 * A function, not a constant, and that is the whole fix. As a module-level
 * object this called `t()` once at import, before the widget had resolved its
 * language, so every English panel rendered its five section headings in
 * Hebrew — and a runtime language switch could not repair it. Same class of
 * bug as the one fixed in features/index.ts; resolve strings at render time.
 */
const groupTitle = (group: GroupId): string => {
  const s = t();
  return {
    text: s.groupText,
    color: s.groupColor,
    motion: s.groupMotion,
    nav: s.groupNav,
    info: s.groupInfo,
  }[group];
};

/** Short forms for the chip row. Resolved at render time, like `groupTitle`. */
const chipLabel = (group: GroupId): string => {
  const s = t();
  return {
    text: s.chipText,
    color: s.chipColor,
    motion: s.chipMotion,
    nav: s.chipNav,
    info: s.chipInfo,
  }[group];
};

let uid = 0;
const nextId = (prefix: string) => `a11y-${prefix}-${++uid}`;

export class Panel {
  readonly root: HTMLElement;
  /** Re-reads state into the controls. Called after any change and on open. */
  private syncers: Array<() => void> = [];

  constructor(
    private readonly config: WidgetConfig,
    private readonly handlers: PanelHandlers,
  ) {
    this.root = this.build();
  }

  sync(): void {
    for (const s of this.syncers) s();
  }

  // ---- Structure --------------------------------------------------------

  private build(): HTMLElement {
    const titleId = nextId('title');

    const panel = el('div', {
      class: 'panel',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': titleId,
      tabindex: '-1',
      lang: currentLang(),
      dir: dirFor(currentLang()),
    });

    // Header
    const closeBtn = el('button', {
      type: 'button',
      class: 'close',
      'aria-label': t().close,
    }, [closeIcon()]);
    closeBtn.addEventListener('click', () => this.handlers.close());

    panel.appendChild(
      el('div', { class: 'header' }, [
        el('h2', { class: 'title', id: titleId, text: t().panelTitle }),
        closeBtn,
      ]),
    );

    // Body
    const body = el('div', { class: 'body' });
    const sections: Array<[GroupId, HTMLElement]> = [
      ['text', this.buildFeatureGroup('text')],
      ['color', this.buildFeatureGroup('color')],
      ['motion', this.buildFeatureGroup('motion')],
      ['nav', this.buildNavGroup()],
      ['info', this.buildInfoGroup()],
    ];
    body.appendChild(this.buildChips(panel, body, sections));
    for (const [, section] of sections) body.appendChild(section);
    panel.appendChild(body);

    panel.appendChild(this.buildFooter());
    return panel;
  }

  /**
   * A sticky row of one chip per section, scrolling the body to its heading.
   *
   * The panel is long enough that "נגישות באתר" sits two screens below the
   * fold on a phone, and the three navigation lists one. Focus moves to the
   * heading as well, so a screen-reader user lands where they asked to go and
   * not back where the chip was. The headings carry `tabindex="-1"` for that
   * and nothing else — they stay out of the Tab order.
   */
  private buildChips(
    panel: HTMLElement,
    body: HTMLElement,
    sections: Array<[GroupId, HTMLElement]>,
  ): HTMLElement {
    const chips = el('div', { class: 'chips', role: 'group', 'aria-label': t().chipsLabel });

    for (const [group, section] of sections) {
      const chip = el('button', { type: 'button', class: 'chip', text: chipLabel(group) });
      chip.addEventListener('click', () => {
        // The body scrolls, except on very short viewports where the stylesheet
        // hands scrolling to the whole panel; whichever one can move, moves.
        // Written as a scrollTop delta rather than scrollIntoView so the host
        // document is never scrolled under the panel.
        const scroller = body.scrollHeight > body.clientHeight ? body : panel;
        section.querySelector<HTMLElement>('.group-title')?.focus({ preventScroll: true });
        scroller.scrollTop +=
          section.getBoundingClientRect().top -
          scroller.getBoundingClientRect().top -
          chips.offsetHeight;
      });
      chips.appendChild(chip);
    }
    return chips;
  }

  private buildGroupHeading(group: GroupId): [string, HTMLElement] {
    const headingId = nextId('grp');
    return [
      headingId,
      el('h3', { class: 'group-title', id: headingId, tabindex: '-1', text: groupTitle(group) }),
    ];
  }

  private buildFeatureGroup(group: GroupId): HTMLElement {
    const [headingId, heading] = this.buildGroupHeading(group);
    const section = el('section', { class: 'group', 'aria-labelledby': headingId }, [heading]);

    for (const feature of getFeatures().filter((f) => f.group === group)) {
      section.appendChild(
        feature.kind === 'toggle'
          ? this.buildToggle(feature)
          : feature.kind === 'choice'
            ? this.buildChoice(feature)
            : this.buildStepper(feature),
      );
    }
    return section;
  }

  // ---- Controls ---------------------------------------------------------

  private buildToggle(feature: ToggleFeature): HTMLElement {
    const descId = nextId('desc');

    const labels = el('span', {}, [
      el('span', { class: 'toggle-label', text: feature.label }),
    ]);
    if (feature.description) {
      labels.appendChild(
        el('span', { class: 'toggle-desc', id: descId, text: feature.description }),
      );
    }

    // `aria-label` fixes the accessible name to the short label, so the longer
    // description is announced as a description rather than folded into the name.
    const btn = el('button', {
      type: 'button',
      class: 'toggle',
      'aria-pressed': 'false',
      'aria-label': feature.label,
      ...(feature.description ? { 'aria-describedby': descId } : {}),
    }, [labels, el('span', { class: 'switch', 'aria-hidden': 'true' })]);

    btn.addEventListener('click', () => {
      const next = this.handlers.getLevel(feature.id) ? 0 : 1;
      if (!this.handlers.setLevel(feature.id, next)) return;
      this.handlers.announce(
        next ? t().announceOn(feature.label) : t().announceOff(feature.label),
      );
    });

    this.syncers.push(() => {
      btn.setAttribute(
        'aria-pressed',
        this.handlers.getLevel(feature.id) ? 'true' : 'false',
      );
    });

    return btn;
  }

  private buildStepper(feature: StepperFeature): HTMLElement {
    const labelId = nextId('lbl');
    const descId = nextId('desc');

    const wrap = el('div', {
      class: 'stepper',
      role: 'group',
      'aria-labelledby': labelId,
      ...(feature.description ? { 'aria-describedby': descId } : {}),
    }, [el('span', { class: 'stepper-label', id: labelId, text: feature.label })]);

    if (feature.description) {
      wrap.appendChild(
        el('span', { class: 'stepper-desc', id: descId, text: feature.description }),
      );
    }

    // Both buttons describe themselves with the current value, so a visitor
    // landing on "+" hears "Increase — Text size, Large" rather than having to
    // find the value between them.
    const valueId = nextId('val');
    const value = el('span', { class: 'step-value', id: valueId });

    // In RTL the first child renders rightmost, so decrease comes first.
    const down = el('button', {
      type: 'button',
      class: 'step-btn',
      'aria-label': `${t().decrease} — ${feature.label}`,
      'aria-describedby': valueId,
    }, ['−']);
    const up = el('button', {
      type: 'button',
      class: 'step-btn',
      'aria-label': `${t().increase} — ${feature.label}`,
      'aria-describedby': valueId,
    }, ['+']);

    // `aria-disabled` rather than `disabled`: a disabled button drops out of the
    // tab order, so a visitor pressing "+" up to the maximum would lose focus
    // entirely at the moment they reach it.
    const step = (delta: number) => {
      const current = this.handlers.getLevel(feature.id);
      const next = Math.max(0, Math.min(feature.max, current + delta));
      if (next === current) return;
      if (!this.handlers.setLevel(feature.id, next)) return;
      // The level name alone ("Large") said nothing about how much further the
      // control goes; the position does.
      this.handlers.announce(
        t().announceLevel(
          feature.label,
          `${feature.stepLabel(next)}, ${t().stepOf(next, feature.max)}`,
        ),
      );
    };
    down.addEventListener('click', () => step(-1));
    up.addEventListener('click', () => step(1));

    const dots = el('div', { class: 'step-dots', 'aria-hidden': 'true' });
    const dotEls: HTMLElement[] = [];
    for (let i = 1; i <= feature.max; i++) {
      const dot = el('span', { class: 'step-dot' });
      dotEls.push(dot);
      dots.appendChild(dot);
    }

    wrap.appendChild(
      el('div', { class: 'stepper-controls' }, [down, value, up]),
    );
    wrap.appendChild(dots);

    this.syncers.push(() => {
      const level = this.handlers.getLevel(feature.id);
      value.textContent = feature.stepLabel(level);
      down.setAttribute('aria-disabled', level === 0 ? 'true' : 'false');
      up.setAttribute('aria-disabled', level === feature.max ? 'true' : 'false');
      dotEls.forEach((dot, i) => {
        dot.className = i < level ? 'step-dot on' : 'step-dot';
      });
    });

    return wrap;
  }

  /**
   * One aria-pressed button per option, exactly one pressed at a time.
   *
   * Pressing the pressed option (other than the first, which is "off" and
   * has nothing to turn off) returns to level 0, so a pressed button still
   * behaves as a pressed button and not as a radio that cannot be cleared.
   */
  private buildChoice(feature: ChoiceFeature): HTMLElement {
    const labelId = nextId('lbl');
    const wrap = el('div', { class: 'stepper', role: 'group', 'aria-labelledby': labelId }, [
      el('span', { class: 'stepper-label', id: labelId, text: feature.label }),
    ]);

    const grid = el('div', { class: 'choices' });
    const buttons = feature.options.map((option, i) => {
      // `aria-label` keeps the CSS tick out of the accessible name.
      const btn = el('button', {
        type: 'button',
        class: 'toggle choice',
        'aria-pressed': 'false',
        'aria-label': option,
      }, [el('span', { class: 'toggle-label', text: option })]);
      btn.addEventListener('click', () => {
        const current = this.handlers.getLevel(feature.id);
        const next = i && current === i ? 0 : i;
        if (next === current) return;
        if (!this.handlers.setLevel(feature.id, next)) return;
        this.handlers.announce(t().announceLevel(feature.label, feature.options[next]!));
      });
      grid.appendChild(btn);
      return btn;
    });
    wrap.appendChild(grid);

    this.syncers.push(() => {
      const level = this.handlers.getLevel(feature.id);
      buttons.forEach((btn, i) => {
        btn.setAttribute('aria-pressed', i === level ? 'true' : 'false');
      });
    });

    return wrap;
  }

  // ---- Navigation aids --------------------------------------------------

  private buildNavGroup(): HTMLElement {
    const [headingId, heading] = this.buildGroupHeading('nav');
    const section = el('section', { class: 'group', 'aria-labelledby': headingId }, [
      heading,
      el('p', { class: 'nav-hint', text: t().navHint }),
    ]);

    section.appendChild(this.buildNavAid(t().navHeadings, getHeadings, t().navCountHeadings));
    section.appendChild(this.buildNavAid(t().navLandmarks, getLandmarks, t().navCountLandmarks));
    section.appendChild(this.buildNavAid(t().navLinks, getLinks, t().navCountLinks));
    return section;
  }

  private buildNavAid(
    label: string,
    read: () => OutlineItem[],
    count: (n: number) => string,
  ): HTMLElement {
    const listId = nextId('list');
    const countEl = el('span', { class: 'nav-count' });

    const btn = el('button', {
      type: 'button',
      class: 'nav-btn',
      'aria-expanded': 'false',
      'aria-controls': listId,
    }, [el('span', { text: label }), countEl, chevronIcon()]);

    const list = el('ul', { class: 'nav-list', id: listId, hidden: true });
    const wrap = el('div', {}, [btn, list]);

    const fill = () => {
      list.textContent = '';
      // Read the page fresh on every expand — SPA routes and lazy content mean
      // a list captured at mount would be stale by the time it is opened.
      const items = read();
      countEl.textContent = count(items.length);

      if (items.length === 0) {
        list.appendChild(el('li', {}, [
          el('span', { class: 'nav-empty', text: t().navEmpty }),
        ]));
        return;
      }

      for (const item of items) {
        const jump = el('button', {
          type: 'button',
          class: 'nav-item',
          style: item.level ? `padding-inline-start:${(item.level - 1) * 12 + 10}px` : null,
          text: item.label,
        });
        jump.addEventListener('click', () => {
          this.handlers.close();
          jumpTo(item.element);
          this.handlers.announce(t().navJumped(item.label));
        });
        list.appendChild(el('li', {}, [jump]));
      }
    };

    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') === 'true';
      if (open) {
        btn.setAttribute('aria-expanded', 'false');
        list.hidden = true;
      } else {
        fill();
        btn.setAttribute('aria-expanded', 'true');
        list.hidden = false;
      }
    });

    // Keep the counts honest without opening: refresh them on every panel open.
    this.syncers.push(() => {
      countEl.textContent = count(read().length);
    });

    return wrap;
  }

  // ---- Site accessibility info -----------------------------------------

  private buildInfoGroup(): HTMLElement {
    const { statementUrl, coordinatorName, coordinatorPhone, coordinatorEmail } = this.config;
    const [headingId, heading] = this.buildGroupHeading('info');
    const section = el('section', { class: 'group', 'aria-labelledby': headingId }, [heading]);

    if (statementUrl) {
      section.appendChild(
        el('p', { class: 'info-row' }, [
          el('a', { href: statementUrl, text: t().statementLink }),
        ]),
      );
    }
    if (coordinatorName) {
      section.appendChild(
        el('p', { class: 'info-row' }, [
          el('span', { class: 'info-label', text: `${t().coordinator}: ` }),
          el('span', { text: coordinatorName }),
        ]),
      );
    }
    if (coordinatorPhone) {
      // The displayed text stays exactly what the site owner typed — that is
      // the form their visitors recognise — while the href is normalised to
      // E.164 so the link also dials from outside Israel.
      //
      // An empty href means the value has no international form (*6050, a
      // 1-800 line, an extension typed into the field). Those numbers are still
      // shown, as text: dropping the row would delete an Israeli site's
      // accessibility contact from the panel over a formatting detail, and
      // would also flip the "nothing configured" branch below.
      const href = telHref(coordinatorPhone);
      section.appendChild(
        el('p', { class: 'info-row' }, [
          el('span', { class: 'info-label', text: `${t().coordinatorPhone}: ` }),
          href
            ? el('a', { href: `tel:${href}`, text: coordinatorPhone })
            : el('span', { text: coordinatorPhone }),
        ]),
      );
    }
    if (coordinatorEmail) {
      section.appendChild(
        el('p', { class: 'info-row' }, [
          el('span', { class: 'info-label', text: `${t().coordinatorEmail}: ` }),
          el('a', { href: `mailto:${coordinatorEmail}`, text: coordinatorEmail }),
        ]),
      );
    }

    // Saying nothing here would let the widget stand in for obligations it does
    // not discharge. If the owner configured neither, the visitor is told that
    // the site is missing something the law requires (PLAN.md §1.4).
    if (!statementUrl && !coordinatorName && !coordinatorPhone && !coordinatorEmail) {
      section.appendChild(el('p', { class: 'info-missing', text: t().infoMissing }));
    }

    return section;
  }

  // ---- Footer -----------------------------------------------------------

  private buildFooter(): HTMLElement {
    const resetBtn = el('button', {
      type: 'button',
      class: 'reset',
      text: t().reset,
    });
    resetBtn.addEventListener('click', () => {
      this.handlers.reset();
      this.handlers.announce(t().resetDone);
    });

    const footer = el('div', { class: 'footer' }, [
      resetBtn,
      // Mandatory. See PLAN.md §2.4 layer 1 — this is the only control that
      // reaches ordinary installers, so it is neither optional nor themeable.
      el('p', { class: 'disclaimer' }, [
        el('strong', { text: t().disclaimerShort }),
        document.createTextNode(' '),
        document.createTextNode(t().disclaimerLong),
      ]),
    ]);

    // Hebrew names in an English sentence read as broken rendering, so the
    // panel swaps to the transliterated forms with the language. A host-supplied
    // `data-by-name` still wins over both — it is their own name, and we are in
    // no position to transliterate it for them.
    const latin = currentLang() !== 'he';
    const authorName = latin ? BRAND.authorNameLatin : BRAND.authorName;
    const byName = this.config.byName ?? (latin ? BRAND.nameLatin : BRAND.name);
    const byUrl = this.config.byUrl ?? BRAND.url;

    // Both links open in a new tab so the visitor never loses the page they
    // were on. `aria-label` carries that fact, because a new tab opening
    // unannounced is disorienting for screen-reader users (WCAG G201).
    // Only the name itself is the link — "תוסף" stays plain text beside it.
    // `noreferrer` because the destination is not ours to vouch for: without it
    // a click handed the host page's URL to whatever analytics the author's
    // site runs, telling us where the widget is installed and that someone
    // opened it. The widget collects nothing, and its links must not either.
    const productLink = el('a', {
      href: byUrl,
      rel: 'noopener noreferrer',
      target: '_blank',
      'aria-label': `${byName} — ${t().newTab}`,
      text: byName,
    });
    const authorLink = el('a', {
      href: BRAND.authorUrl,
      rel: 'noopener noreferrer',
      target: '_blank',
      'aria-label': `${authorName} — ${t().newTab}`,
      text: authorName,
    });

    // The Hebrew line opens with a word ("תוסף שקוף"); the English one starts
    // with the name itself. Appending an empty prefix would leave a stray
    // leading space before the link.
    const line: Node[] = [];
    if (t().byPrefix) line.push(document.createTextNode(`${t().byPrefix} `));
    line.push(productLink, document.createTextNode(` ${t().byMiddle} `), authorLink);

    footer.appendChild(el('p', { class: 'by' }, line));

    return footer;
  }
}
